import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from 'utils/api/endpoints';
import {
  fallbackPDFCatalog,
  pdfOptionDefaults,
  validatePDFCatalog,
} from 'utils/pdfOptions';
import { getInitialPrintPaperSize } from 'utils/printPaperSizes';

export default function usePDFOptions(budgetId, templateName) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!budgetId) return undefined;
    let active = true;
    setError(null);
    setState(null);
    const load = async () => {
      let catalog;
      let live = true;
      try {
        catalog = validatePDFCatalog(
          (await API_ENDPOINTS.budgets.get_pdf_options(budgetId)).data
        );
      } catch (failure) {
        if (failure.response?.status !== 404) throw failure;
        catalog = fallbackPDFCatalog(templateName);
        live = false;
      }
      const values = pdfOptionDefaults(catalog);
      const paper = catalog.groups
        .flatMap(group => group.options)
        .find(option => option.key === 'paper_size');
      const preferredPaper = getInitialPrintPaperSize(paper?.choices);
      if (paper?.choices.some(choice => choice.value === preferredPaper))
        values.paper_size = preferredPaper;
      if (active) setState({ budgetId, catalog, values, live });
    };
    load().catch(failure => {
      if (active)
        setError(
          failure.response?.data?.detail ||
            failure.message ||
            'Could not load PDF options.'
        );
    });
    return () => {
      active = false;
    };
  }, [budgetId, templateName, attempt]);
  const change = useCallback((key, value) => {
    setState(
      current =>
        current && { ...current, values: { ...current.values, [key]: value } }
    );
  }, []);
  return {
    ...(state?.budgetId === budgetId ? state : {}),
    change,
    error,
    retry: () => setAttempt(value => value + 1),
  };
}
